import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchAndContinue } from 'src/app/lego-utils';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { ItemStructure } from 'src/app/models';
import { LegoRestApiDataService } from 'src/app/services';
import { EquipmentFacadeService } from 'src/app/services/equipment-facade.service';

@Injectable({ providedIn: 'root' })
export class MouldDesignService {
  private readonly itemStructureSubject = new BehaviorSubject<ItemStructure | undefined>(undefined);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<string | undefined>(undefined);

  public readonly itemStructure$ = this.itemStructureSubject.asObservable();
  public readonly loading$ = this.loadingSubject.asObservable();
  public readonly error$ = this.errorSubject.asObservable();

  public constructor(
    private readonly legoRestApiDataService: LegoRestApiDataService,
    private readonly equipmentFacade: EquipmentFacadeService
  ) {
    combineLatest([
      this.equipmentFacade.selectedEquipmentId$,
      this.equipmentFacade.equipmentDetails$,
    ])
      .pipe(
        filter(([id, details]) => !!id && !!details && details.equipmentNumber === id),
        distinctUntilChanged(
          ([, prevDetails], [, currDetails]) =>
            prevDetails?.equipmentNumber === currDetails?.equipmentNumber
        ),
        tap(() => {
          this.itemStructureSubject.next(undefined);
          this.errorSubject.next(undefined);
          this.loadingSubject.next(true);
        }),
        switchMap(([, details]) =>
          catchAndContinue(
            this.legoRestApiDataService.getMouldDesignItemStructureByConstructionId(
              details?.constructionId ?? ''
            ),
            (error) => {
              this.itemStructureSubject.next(undefined);

              this.errorSubject.next(
                error instanceof HttpErrorResponse ? error.statusText : 'Unknown error'
              );
            }
          )
        ),
        tap((itemStructure) => {
          this.itemStructureSubject.next(itemStructure);
          this.loadingSubject.next(false);
        })
      )
      .subscribe();
  }
}
